import React, { useEffect, useMemo, useState } from 'react';
import { TrackJS } from 'trackjs';
import { useDispatch } from "react-redux";
import styled from "styled-components";
import { addVisitor, MeetingRoomBookingViewPanel, setDialogPanel } from "../../models/meeting-room-booking-dialog.duck";
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { HeadlineMedium } from '@shared/ui/atoms/fonts/Headline';
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import TextField from '@shared/ui/atoms/TextField';
import { useTranslation } from "react-i18next";
import { UserSearchDropDownList } from '@shared/ui/molecules/UserSearchDropDownList';
import { BasicUserInfo } from '@shared/models/user.models';
import { BodyRegular } from '@shared/ui/atoms/fonts/Body';
import Colours from '@shared/ui/atoms/Colours';
import { Time, TimeRangeSelectorV2 } from "../../components/TimeRangeSelectorV2";
import { UserAvatarAndName } from '@shared/ui/molecules/UserAvatarAndName';
import { warningNotification } from '@shared/store/notification.duck';
import {
  DropDownListItem,
  ReactSelectDropDownList
} from '@shared/ui/organisms/drop-down-list/ReactSelectDropDownList';
import { getRecentVisitors, RecentVisitor } from "@features/whereabouts/pages/company-movements/components/visitor-booking/data/visitor-booking-service";

export function AddRemoveVisitorsPanel(props: Props) {
  const dispatch = useDispatch();
  const {t} = useTranslation();
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [dueIn, setDueIn] = useState<Time>({hour: 9, minutes: 0});
  const [visitingUser, setVisitingUser] = useState<BasicUserInfo | undefined>();
  const [recentVisitors, setRecentVisitors] = useState<RecentVisitor[]>([]);

  useEffect(() => {
    getRecentVisitors()
      .then(setRecentVisitors)
      .catch((e: any) => {
        // Recent visitors are a convenience list — keep the panel usable, but track the failure.
        TrackJS?.track(`[AddRemoveVisitorsPanel] getRecentVisitors failed: ${String(e?.message ?? e).slice(0, 200)}`);
      });
  }, []);

  const recentVisitorOptions: DropDownListItem[] = useMemo(() => {
    return recentVisitors.map(v => ({
      value: v,
      label: v.companyName ? `${v.visitorName} (${v.companyName})` : v.visitorName,
    }));
  }, [recentVisitors]);

  const onRecentVisitorSelected = (selection: DropDownListItem) => {
    if (selection?.value) {
      const visitor = selection.value as RecentVisitor;
      setName(visitor.visitorName || '');
      setCompanyName(visitor.companyName || '');
      setEmail(visitor.email || '');
    }
  }

  const onCancelClicked = () => {
    dispatch(setDialogPanel(MeetingRoomBookingViewPanel.MeetingRoomBooking));
  }

  const onFinishedClicked = () => {
    if (name.trim().length === 0) {
      dispatch(warningNotification('Please enter a name'))
      return;
    }

    dispatch(setDialogPanel(MeetingRoomBookingViewPanel.MeetingRoomBooking));

    let visitor = {
      visitorName: name.trim(),
      companyName: companyName,
      email: email,
      timeDueHours: dueIn.hour,
      timeDueMinutes: dueIn.minutes,
      visitingUserId: visitingUser?.userId,
    };

    dispatch(addVisitor({visitor: visitor}));
  }

  const userSelected = (user: BasicUserInfo) => {
    setVisitingUser(user);
  }

  const onDueInTimeChanged = (dueInTime: Time) => {
    setDueIn(dueInTime)
  }

  return (
    <>
      <Content>
        <HeadlineMedium style={{marginBottom: 12}}>{t('hospitality.add-visitor.title')}</HeadlineMedium>

        {recentVisitorOptions.length > 0 && <>
          <BodyRegular colour={Colours.darkGrey}
                       weight={600}
                       style={{marginBottom: 8}}>{t('hospitality.add-visitor.recent-visitors')}</BodyRegular>
          <RecentVisitorDropdownWrapper>
            <ReactSelectDropDownList items={recentVisitorOptions}
                                     onItemSelected={onRecentVisitorSelected} />
          </RecentVisitorDropdownWrapper>
        </>}

        <TextField onChange={setName}
                   fullWidth={true}
                   value={name}
                   label={'general.full-name'} />

        <TextField onChange={setCompanyName}
                   fullWidth={true}
                   value={companyName}
                   label={'hospitality.add-visitor.company'} />

        <TextField onChange={setEmail}
                   fullWidth={true}
                   value={email}
                   label={'hospitality.add-visitor.email'} />

        <BodyRegular colour={Colours.darkGrey}
                     weight={600}
                     style={{marginBottom: 8}}>{t('hospitality.add-visitor.due-in')}</BodyRegular>
        <TimeRangeSelectorV2 fromTime={{ hour: 9, minutes: 0 }}
                             toTime={{hour: 24, minutes: 0}}
                             hideLabels={true}
                             onTimeChange={onDueInTimeChanged}
                             hideTimeTo={true} />

        <BodyRegular colour={Colours.darkGrey}
                     weight={600}
                     style={{marginBottom: 8}}>{t('visitor-booking.meeting-with')}</BodyRegular>
        <UserSearchDropDownList closeOnSelect={true} onSelect={userSelected} menuPlacement={'top'} />

        {visitingUser && <VisitingUserWrapper>
          <UserAvatarAndName colour={'darkBlue'}
                             firstName={visitingUser.firstName}
                             lastName={visitingUser.lastName} />
        </VisitingUserWrapper>}

        <Controls>
          <OutlineButton text={'button.cancel'}
                         click={onCancelClicked} />
          <PrimaryButton text={'hospitality.add-visitor.add-visitor'}
                         style={{marginLeft: 12, paddingLeft: 48, paddingRight: 48}}
                         click={onFinishedClicked} />
        </Controls>
      </Content>
    </>
  )
}

interface Props {
}


const Content = styled.div`
  padding: 24px 56px 0 56px;
  flex: 1;
  display: flex;
  flex-direction: column;
  .fromTimeRangeSelector__inputContainer {
    margin: 0 0 16px 0 !important;
  }
`

const Controls = styled.div`
  display: flex;
  margin-top: auto;
  padding-top: 24px;
  padding-bottom: 24px;
  margin-bottom: 36px;
  justify-content: flex-end;
  button {
    width: unset;
  }
`

const VisitingUserWrapper = styled.div`
  margin: 24px 0 48px 0;
`

const RecentVisitorDropdownWrapper = styled.div`
  margin-bottom: 16px;
`
